import React, { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { 
  XMarkIcon, 
  CurrencyDollarIcon,
  InformationCircleIcon,
  CalculatorIcon
} from '@heroicons/react/24/outline'
import { loanService, Loan, CreateLoanPaymentData } from '@/services/loanService'
import toast from 'react-hot-toast'

interface LoanPaymentModalProps {
  loan: Loan
  onClose: () => void
  onSuccess: () => void
}

const LoanPaymentModal: React.FC<LoanPaymentModalProps> = ({ 
  loan, 
  onClose, 
  onSuccess 
}) => {
  const [formData, setFormData] = useState({
    paymentAmount: '',
    paymentMethod: 'salary_deduction' as 'salary_deduction' | 'bank_transfer' | 'cash' | 'cheque' | 'mobile_money',
    paymentReference: '',
    notes: ''
  })

  const queryClient = useQueryClient()

  // Record payment mutation
  const recordPaymentMutation = useMutation({
    mutationFn: async (data: CreateLoanPaymentData) => {
      return loanService.recordLoanPayment(data)
    },
    onSuccess: () => {
      toast.success('Payment recorded successfully')
      queryClient.invalidateQueries({ queryKey: ['loans'] })
      queryClient.invalidateQueries({ queryKey: ['loanApplications'] })
      onSuccess()
      onClose()
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Failed to record payment')
    }
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    const amount = parseFloat(formData.paymentAmount)
    
    if (!formData.paymentAmount || amount <= 0) {
      toast.error('Please enter a valid payment amount')
      return
    }

    if (amount > loan.outstandingBalance) {
      toast.error(`Payment amount cannot exceed outstanding balance of ${formatCurrency(loan.outstandingBalance)}`)
      return
    }

    if (formData.paymentMethod !== 'salary_deduction' && !formData.paymentReference.trim()) {
      toast.error('Please provide a payment reference')
      return
    }

    const paymentData: CreateLoanPaymentData = {
      loanId: loan.id,
      paymentAmount: amount,
      paymentMethod: formData.paymentMethod,
      ...(formData.paymentReference.trim() && { paymentReference: formData.paymentReference.trim() }),
      ...(formData.notes.trim() && { notes: formData.notes.trim() })
    }

    recordPaymentMutation.mutate(paymentData)
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString()
  }

  const calculateNewBalance = () => {
    const paymentAmount = parseFloat(formData.paymentAmount) || 0
    return Math.max(0, loan.outstandingBalance - paymentAmount)
  }


  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
        <div className="flex items-center justify-between pb-3 border-b">
          <h3 className="text-lg font-medium text-gray-900">
            Record Loan Payment
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="mt-4">
          {/* Loan Summary */}
          <div className="bg-blue-50 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <InformationCircleIcon className="h-5 w-5 text-blue-600 mr-2 mt-0.5" />
              <div className="flex-1">
                <div className="font-medium text-blue-900 mb-2">Loan Details</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-800">
                  <div>
                    <div><strong>Employee:</strong> {loan.employee.firstName} {loan.employee.lastName}</div>
                    <div><strong>Loan Type:</strong> {loan.loanType.name}</div>
                    <div><strong>Principal:</strong> {formatCurrency(loan.principalAmount)}</div>
                  </div>
                  <div>
                    <div><strong>Outstanding:</strong> {formatCurrency(loan.outstandingBalance)}</div>
                    <div><strong>Total Paid:</strong> {formatCurrency(loan.totalPaid)}</div>
                    <div><strong>Monthly Payment:</strong> {formatCurrency(loan.monthlyPayment)}</div>
                  </div>
                </div>
                {loan.nextPaymentDate && (
                  <div className="mt-2 text-sm text-blue-800">
                    <strong>Next Payment Due:</strong> {formatDate(loan.nextPaymentDate)}
                  </div>
                )}
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Payment Amount */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Payment Amount (USD) *
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <CurrencyDollarIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={loan.outstandingBalance}
                  value={formData.paymentAmount}
                  onChange={(e) => setFormData({ ...formData, paymentAmount: e.target.value })}
                  className="block w-full pl-10 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  placeholder="0.00"
                  required
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Maximum: {formatCurrency(loan.outstandingBalance)}
              </p>
            </div>

            {/* Payment Method */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Payment Method *
              </label>
              <select
                value={formData.paymentMethod}
                onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value as any })}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                required
              >
                <option value="salary_deduction">Salary Deduction</option>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="cash">Cash</option>
                <option value="cheque">Cheque</option>
                <option value="mobile_money">Mobile Money</option>
              </select>
            </div>

            {/* Payment Reference */}
            {formData.paymentMethod !== 'salary_deduction' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Reference *
                </label>
                <input
                  type="text"
                  value={formData.paymentReference}
                  onChange={(e) => setFormData({ ...formData, paymentReference: e.target.value })}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  placeholder="Transaction ID, Receipt number, etc."
                  required
                />
              </div>
            )}

            {/* Notes */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes (Optional)
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                placeholder="Additional notes about this payment..."
              />
            </div>

            {/* Payment Preview */}
            {formData.paymentAmount && parseFloat(formData.paymentAmount) > 0 && (
              <div className="mb-4 p-3 bg-green-50 rounded-lg">
                <div className="flex items-center">
                  <CalculatorIcon className="h-5 w-5 text-green-600 mr-2" />
                  <div className="text-sm">
                    <div className="font-medium text-green-900 mb-1">Payment Preview:</div>
                    <div className="text-green-800 space-y-1">
                      <div>Payment Amount: <span className="font-semibold">{formatCurrency(parseFloat(formData.paymentAmount))}</span></div>
                      <div>New Outstanding Balance: <span className="font-semibold">{formatCurrency(calculateNewBalance())}</span></div>
                      {calculateNewBalance() === 0 && (
                        <div className="text-green-700 font-medium">✓ This payment will complete the loan</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={recordPaymentMutation.isPending || !formData.paymentAmount}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-50"
              >
                {recordPaymentMutation.isPending ? 'Recording...' : 'Record Payment'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default LoanPaymentModal